import { useState, useEffect, lazy, Suspense } from "react";
const Table = lazy(() => import('@/components/Table'));
const Button = lazy(() => import('@/components/Button'));
const Item = lazy(() => import('@/pages/Item'));

import { getNeedsSelection, getTableParam, checkViewType, checkViewItem, replaceIfItemView } from '@/utils/Table';

export default function GenericTable({t, language, user, token, handleLogin, router, apiUrl, table, 
    getData, setData, deleteData, sendMessage, fullScreen, fullPage, setFullPage, openUrlModal
}){

    const [ api, setApi ] = useState(null);

    // response from the api when getting data
    const [ response, setResponse ] = useState(null);

    const [ loading, setLoading ] = useState(false);
    const [ columns, setColumns ] = useState(null);
    const [ tableRows, setTableRows ] = useState(null);

    // save table config
    const [ config, setConfig ] = useState(null);

    // set if editable and modal type. by default it is editable and modal
    const [ editable, setEditable ] = useState(true);
    const [ modalDefault ] = useState(true);
    const [ modal, setModal ] = useState(true);

    const [ needsSelection, setNeedsSelection ] = useState(null);
    const [ selectionItem, setSelectionItem ] = useState(null);

    // check if we are on /view/N to show only the info for that item
    // although we get all the data because it makes no sense to call backend only for 1 row and
    // call it later to go to the table with all the rows
    const [ viewPath, setViewPath ] = useState(checkViewType(router.pathname));
    const [ itemDetails, setItemDetails ] = useState(null);

    const resetData = () => {
        if(columns && Object.keys(columns).length > 0){
            setColumns(null);
        }

        if(tableRows && tableRows.length > 0){
            setTableRows(null);
        }
    };

    /*
    // to handle when clicking on the view button or double click the row
    const [ hasItemAction ] = useState(false);//useState(table === 'planifications' ? true : false);
    const handleItemAction = (params) => {

        if(params){

            const item = {
                api: params?.api,
                node: params?.node
            };

            // go to item details
            //setSelectedItem(item);

            // change url to can copy the url and open directly to the ticket
            var url = '/'+table+'/view/'+params?.node?.data?.id;
            router.navigate(url);
            //window.history.pushState({path:url},'',url);
        }
        else{
            sendMessage(t('error.dataretrievingerror'),'error');
        }
    }
    */

    // if changing the path, check if view item
    useEffect(() => {
        if(router.pathname){
            // if clicking on menu and in item details, go back to list
            const { viewType } = checkViewItem(router);
            if(!viewType){
                setItemDetails(null);
            }
        }
    }, [router.pathname]);

    // get data
    useEffect(() => {        

        // if on menu/submenu where the parent menu needs a selection          
        var needsSelectionCheck = getNeedsSelection(router.pathname,table);
        
        var retrieveData = true;
        if(needsSelectionCheck){

            setNeedsSelection(needsSelectionCheck);

            // check if item selected
            var itemInfo = getTableParam(user,needsSelectionCheck,'selected'); 
            setSelectionItem(itemInfo);    

            // if on subitem and item not selected
            if(needsSelectionCheck !== table && !itemInfo?.id){
                retrieveData = false;
            }           
        }
        else{
            setNeedsSelection(null);
            setSelectionItem(null);
        }

        if(retrieveData){

            // set null to get the new item if on item view
            setItemDetails(null);

            // check if view item details
            const { viewType, viewItemId } = checkViewItem(router);
            setViewPath(viewType);

            // fullview does not have back button, so get only 1 row, not all
            var itemId = null;
            if(viewType === 'fullview'){
                itemId = viewItemId;
            }

            handleGet(router.pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setItemDetails,itemId);
        }

    }, [table]);

    // to retrieve the data
    const handleGet = (pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setItemDetails = null,itemId = null) => {
        getData(pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setItemDetails,itemId);
    }

    // refresh data    
    const handleRefresh = () => {        
        handleGet(router.pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal);
    }

    // add/edit a row
    // if inline edition, we use setSavedEnabled
    // if modal edition, we use handleCloseItemModal
    const handleEdit = (action,api,node,data,setSavedEnabled = null,setAddDisabled = null,handleCloseItemModal = null,setRender = null, handleTableItemSelected = null) => {        
        setData(action,router.pathname,table,api,node,modal,data,columns,setTableRows,setSavedEnabled,setAddDisabled,handleCloseItemModal,setRender,handleTableItemSelected);
    }

    // delete a row
    const handleDelete = (selectedRows,api,handleCloseItemModal = null,setRender = null, handleTableItemSelected = null) => {
        deleteData(router.pathname,table,selectedRows,api,columns,setTableRows,handleCloseItemModal,setRender,handleTableItemSelected);
    }

    const handleBack = () => {
        
        // remove item to show items list
        setItemDetails(null);

        // update link
        const path = replaceIfItemView(router.pathname);
        router.navigate(path); 
    }      
    
    return (
        <div className={`h-full ${viewPath === 'fullview' ? 'fullview' : ''}`}>
            {
                needsSelection && needsSelection !== table && !selectionItem?.id ? 
                    <div className=''>
                        <div className='text-center my-4'>
                            {t('actions.notselected.text',{table:t(needsSelection+'.menu.title')})}
                        </div>
                        <Suspense>
                            <Button
                                //window.location.href='/planifications'
                                onClick={() => router.navigate('/'+needsSelection)}
                                text={t('actions.notselected.button')}
                                type="select"
                                variant="contained"                            
                                sx={{width:'100%'}}
                            />
                        </Suspense>
                    </div>
                :
                    itemDetails ? 
                        <Suspense>
                            <Item
                                t={t}                                
                                table={table}
                                columns={columns}
                                item={itemDetails}
                                back={viewPath === 'view' ? '/'+table : null}
                                handleBack={handleBack}
                                viewPath={viewPath}                                
                                openUrlModal={openUrlModal}
                            />
                        </Suspense>
                    :
                        (columns && Object.keys(columns).length > 0) &&
                        (tableRows !== null) &&
                        <Suspense>
                            <Table     
                                api={api}
                                setApi={setApi}  
                                apiUrl={apiUrl}                          
                                loading={loading}
                                t={t}
                                language={language}
                                user={user}
                                token={token}
                                handleLogin={handleLogin}
                                router={router}                                
                                table={table}
                                config={config}
                                editable={editable}
                                modal={modal}
                                rows={tableRows}
                                columns={columns}
                                response={response}                                
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                                handleRefresh={handleRefresh}
                                sendMessage={sendMessage}  
                                fullScreen={fullScreen}
                                fullPage={fullPage}
                                setFullPage={setFullPage}
                                openUrlModal={openUrlModal}
                                //handleItemAction={hasItemAction ? handleItemAction : null}
                            />
                        </Suspense>              
            }
        </div>
    );
}